import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { applyBranding, clearBranding } from '../utils/branding';
import { setOrgManifest, clearOrgManifest } from '../utils/pwaManifest';

/**
 * InviteAccept — Public landing page for /invite/:token
 *
 * Recipients of a workspace invite email arrive here. We:
 *   1) Look up the invite by token to render the inviting org's branding,
 *      role, and personal note so the page feels native to that workspace.
 *   2) Collect minimum-required join fields (full name, password, phone).
 *   3) Call the public acceptance endpoint, which either creates a new
 *      account or links the email to the inviting org with the invite
 *      role. The endpoint issues a JWT — we drop straight into `/`.
 *
 * Edge cases handled:
 *   - 404 / 410 token states show a clean retry CTA instead of crashing.
 *   - If the invited email already has an account, the password field is
 *     optional (existing users can keep their password); we still ask for
 *     the full name + phone to confirm the account belongs to them.
 *
 * Props:
 *   onLogin(userData, token) — called on successful acceptance so the App
 *     shell can swap to the dashboard immediately.
 */
function InviteAccept({ onLogin }) {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState('');

  const [form, setForm] = useState({
    full_name: '',
    password: '',
    confirm_password: '',
    phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // --- Branding bootstrap -------------------------------------------------
  // Fetch invite, then apply that org's brand colours + manifest so the
  // page (and any subsequent /login fallback) feels like the right tenant.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await api.getInviteByToken(token);
        if (cancelled) return;
        setInvite(data);
        const org = data?.organization || {};
        applyBranding({
          primary_color: org.primary_color,
          secondary_color: org.secondary_color,
          logo_url: org.logo_url,
          welcome_message: org.welcome_message,
          tagline: org.tagline,
          name: org.name,
          slug: org.slug,
        });
        if (org.slug) setOrgManifest(org.slug);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message || 'This invite is no longer valid.');
          clearBranding();
          clearOrgManifest();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const roleLabel = useMemo(() => {
    const r = invite?.role || '';
    return r ? r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';
  }, [invite]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    const fullName = form.full_name.trim();
    const phone = form.phone.trim();
    const password = form.password;
    const confirm = form.confirm_password;

    if (!fullName) {
      setSubmitError('Please enter your full name.');
      return;
    }
    if (!phone || phone.replace(/\D/g, '').length < 7) {
      setSubmitError('Please enter a valid contact phone number.');
      return;
    }
    if (password && password.length < 6) {
      setSubmitError('Password must be at least 6 characters.');
      return;
    }
    if (password && confirm !== password) {
      setSubmitError('Passwords do not match.');
      return;
    }
    if (!password) {
      // Brand-new accounts MUST set a password; if a backend later says
      // the email already exists it's accepted without rotating.
      setSubmitError('Please choose a password (at least 6 characters).');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.acceptInviteByToken(token, {
        full_name: fullName,
        password,
        phone,
      });
      // Persist + hand off to App shell.
      localStorage.setItem('token', res.access_token);
      // Flag first-run so ParentDashboard opens the Enrollment Wizard
      // for freshly-onboarded parents instead of stranding them on an
      // empty Children tab. Only meaningful for the parent role.
      try {
        const inviteRole = (invite?.role || res?.user?.role || '').toString();
        if (inviteRole === 'parent') {
          sessionStorage.setItem('kw_just_registered', '1');
        }
      } catch (_) { /* private mode / SSR safety */ }
      try {
        const me = await api.getMe();
        onLogin?.(me, res.access_token);
      } catch {
        onLogin?.(res.user, res.access_token);
      }
      navigate('/');
    } catch (err) {
      setSubmitError(err.message || 'Failed to accept invite');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- States ------------------------------------------------------

  if (loading) {
    return (
      <div className="login-container" data-testid="invite-page-loading">
        <div className="loading-screen" style={{ minHeight: '60vh' }}>
          <div className="spinner" />
          <p>Loading invitation…</p>
        </div>
      </div>
    );
  }

  if (loadError || !invite) {
    return (
      <div className="login-container" data-testid="invite-page-error">
        <div className="card" style={{ maxWidth: 460, margin: '4rem auto', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⚠️</div>
          <h2 style={{ margin: '0 0 0.5rem 0' }}>Invitation unavailable</h2>
          <p style={{ color: 'hsl(var(--muted-foreground))' }}>
            {loadError || 'This invite link is no longer valid.'}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="invite-error-login-btn"
            onClick={() => navigate('/login')}
            style={{ marginTop: '1rem' }}
          >
            Go to sign-in
          </button>
        </div>
      </div>
    );
  }

  const org = invite.organization || {};

  return (
    <div className="login-container" data-testid="invite-accept-page">
      <div
        className="card"
        style={{
          maxWidth: 460,
          margin: '3rem auto',
          padding: '2rem 1.75rem',
        }}
      >
        {/* Branded header */}
        <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
          {org.logo_url ? (
            <img
              src={org.logo_url}
              alt={`${org.name || 'Workspace'} logo`}
              style={{ height: 48, marginBottom: 12 }}
              data-testid="invite-org-logo"
            />
          ) : (
            <div style={{ fontSize: '2.2rem', marginBottom: 6 }}>✉️</div>
          )}
          <h1 style={{ margin: 0, fontSize: '1.4rem' }} data-testid="invite-org-name">
            Join {org.name || 'this workspace'}
          </h1>
          <p style={{ margin: '0.5rem 0 0', color: 'hsl(var(--muted-foreground))', fontSize: '0.95rem' }}>
            You've been invited as <strong>{roleLabel}</strong>
            {invite.invited_by_name ? <> by <strong>{invite.invited_by_name}</strong></> : null}.
          </p>
          {invite.personal_message && (
            <blockquote
              data-testid="invite-personal-message"
              style={{
                margin: '1rem auto 0',
                padding: '0.65rem 0.9rem',
                background: 'hsl(var(--muted) / 0.5)',
                borderLeft: '3px solid hsl(var(--primary))',
                borderRadius: '6px',
                textAlign: 'left',
                fontSize: '0.9rem',
                color: 'hsl(var(--foreground))',
                fontStyle: 'italic',
              }}
            >
              “{invite.personal_message}”
            </blockquote>
          )}
        </div>

        <form onSubmit={handleSubmit} data-testid="invite-accept-form">
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={invite.email || ''}
              readOnly
              data-testid="invite-email-field"
              style={{ ...inputStyle, background: 'hsl(var(--muted) / 0.4)' }}
            />
            <small style={{ color: 'hsl(var(--muted-foreground))' }}>
              Locked to the invited address.
            </small>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Full name *</label>
            <input
              type="text"
              required
              autoComplete="name"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="invite-fullname-input"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Phone number *</label>
            <input
              type="tel"
              required
              autoComplete="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+44…"
              data-testid="invite-phone-input"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Choose a password *</label>
            <input
              type="password"
              required
              autoComplete="new-password"
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="invite-password-input"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Confirm password *</label>
            <input
              type="password"
              required
              autoComplete="new-password"
              minLength={6}
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              data-testid="invite-confirm-input"
              style={inputStyle}
            />
          </div>

          {submitError && (
            <div
              role="alert"
              data-testid="invite-submit-error"
              style={{
                background: 'hsl(0 75% 95%)',
                color: 'hsl(0 60% 35%)',
                padding: '0.55rem 0.7rem',
                borderRadius: 'var(--radius)',
                marginBottom: '0.75rem',
                fontSize: '0.9rem',
              }}
            >
              {submitError}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
            data-testid="invite-accept-submit"
            style={{ width: '100%', padding: '0.7rem' }}
          >
            {submitting ? 'Joining…' : `Accept invitation & join ${org.name || 'workspace'}`}
          </button>
        </form>

        <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
          By accepting you agree to the workspace terms and conditions
          {org.slug ? (
            <>
              {' '}—{' '}
              <button
                type="button"
                onClick={() => navigate(`/login/${org.slug}`)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'hsl(var(--primary))',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline',
                  font: 'inherit',
                }}
                data-testid="invite-already-have-account"
              >
                already have an account?
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  border: '1px solid hsl(var(--border))',
  borderRadius: 'var(--radius)',
  background: '#fff',
  fontSize: '0.95rem',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  marginBottom: '0.25rem',
  fontSize: '0.85rem',
  fontWeight: 500,
};

export default InviteAccept;
